import type React from "react";

import { Trans, useTranslation, withTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { MegaphoneIcon } from "@lucide/icons";
import { formatDistanceToNow } from "date-fns";

import { i18n } from "simplydo/core/i18n";
import { getNotificationLocation } from "simplydo/core/notificationLocation";
import { ImageWithFallback } from "simplydo/react/components/ImageWithFallback";
import { Icon } from "simplydo/react/ui/icon";
import { Loader } from "simplydo/react/ui/loader";
import { Menu, MenuItem } from "simplydo/react/ui/menu";

import api from "api";
import util from "utils/utils";

import type {
  AnnouncementNotification,
  AppNotification,
  NotificationContentProps,
  NotificationEvent,
  NotificationInvitation,
  NotificationTrayUser,
} from "../types";
import AnnouncementReport from "./AnnouncementReport";
import AiChallengeFormatContent from "./Challenges/AiChallengeFormat";
import AiFormatReviewContent from "./Challenges/AiFormatReview";
import AssessorAssignedContent from "./Challenges/AssessorAssigned";
import ChallengeAddedToCalendar from "./Challenges/ChallengeAddedToCalendar";
import ChallengeClosed from "./Challenges/ChallengeClosed";
import ChallengeInnointelSearchContent from "./Challenges/ChallengeInnointelSearch";
import ChallengeTranslateContent from "./Challenges/ChallengeTranslate";
import ClosingSoonContent from "./Challenges/ClosingSoon";
import RequestOpen from "./Challenges/RequestOpen";
import StageChangedContent from "./Challenges/StageChanged";
import DiscussionApproval from "./Group/DiscussionApproval";
import DiscussionPollVotedContent from "./Group/DiscussionPollVoted";
import DiscussionPostedContent from "./Group/DiscussionPosted";
import DiscussionReacted from "./Group/DiscussionReacted";
import DiscussionReplied from "./Group/DiscussionReplied";
import GroupAddedToCalendar from "./Group/GroupAddedToCalendar";
import GroupInvitedContent from "./Group/Invited";
import InvitedActions from "./Group/InvitedActions";
import GroupJoinRequestedContent from "./Group/JoinRequested";
import JoinRequestedActions from "./Group/JoinRequestedActions";
import Joined from "./Group/Joined";
import AiIdeaAnonymityCheckContent from "./Idea/AiIdeaAnonymityCheck";
import AiIdeaFeedbackContent from "./Idea/AiIdeaFeedback";
import AddedAsCollaboratorContent from "./Idea/CollaboratorAdded";
import CommentReacted from "./Idea/CommentReacted";
import IdeaCommentReply from "./Idea/CommentReply";
import IdeaCommentedContent from "./Idea/Commented";
import DeadlineApproaching from "./Idea/DeadlineApproaching";
import DeadlineSet from "./Idea/DeadlineSet";
import IdeaSendEmail from "./Idea/IdeaSendEmail";
import IdeaSubmittedContent from "./Idea/Submitted";
import IdeaVotedContent from "./Idea/Voted";
import {
  IdeaBusinessProfileInvited,
  IdeaBusinessProfileInvitedActions,
} from "./IdeaBusinessProfileInvited";
import MentionedContent from "./Mentioned";
import PollClosed from "./PollClosed";
import ProjectAssignedAssignee from "./Project/ProjectAssignedAssignee";
import ProjectAssignedManager from "./Project/ProjectAssignedManager";
import ProjectIdeaCommented from "./Project/ProjectIdeaCommented";
import ProjectIdeaMoved from "./Project/ProjectIdeaMoved";
import ProjectIdeaReplied from "./Project/ProjectIdeaReplied";
import UnlockedAchievementContent from "./UnlockedAchievement";

const mentions = [
  {
    type: "mentionedDiscussionComment",
    message: "mentioned you in a discussion",
    link: "comments",
    location: "",
    id: "discussion",
  },
  {
    type: "mentionedIdeaComment",
    message: `mentioned you in ${i18n.t("generic.ideaWithArticle")} comment`,
    link: "ideas",
    location: "",
    id: "idea",
  },
  {
    type: "mentionedIdeaProjectComment",
    message: "mentioned you in a project board comment",
    link: "challenges",
    location: "board",
  },
];

const notificationAllowlist = [
  "ideaCommented",
  "ideaSubmitted",
  "ideaVoted",
  "ideaSendEmail",
  "collaboratorAdded",
  "mentionedDiscussionComment",
  "mentionedIdeaComment",
  "mentionedIdeaProjectComment",
  "closingSoon",
  "stageChanged",
  "challengeAddedToCalendarEvent",
  "groupAddedToCalendarEvent",
  "challengeClosed",
  "requestOpenChallenge",
  "discussionPosted",
  "reactedDiscussionComment",
  "discussionCommentReply",
  "discussionPostRequiresApproval",
  "unlockedAchievement",
  "discussionPollVoted",
  "projectIdeaCommented",
  "projectIdeaReplied",
  "projectIdeaMoved",
  "projectAssignedAssignee",
  "projectAssignedManager",
  "assessorAssigned",
  "groupJoinRequested",
  "groupInvited",
  "groupUserJoined",
  "ideaCommentReply",
  "reactedIdeaComment",
  "pollClosed",
  "announcementReport",
  "ideaBusinessProfileInvited",
  "ideaDeadlineApproaching",
  "ideaDeadlineSet",
  "announcement",
  "challengeTranslate",
  "aiChallengeFormat",
  "aiFormatReview",
  "challengeInnointelSearch",
  "aiIdeaFeedback",
  "aiIdeaAnonymityCheck",
];

const actionableNotifications = [
  "groupJoinRequested",
  "groupInvited",
  "ideaBusinessProfileInvited",
];

const AvatarImage = (source: string) => (
  <ImageWithFallback
    style={{
      width: 50,
      height: 50,
      objectFit: "cover",
      borderRadius: 10,
      backgroundColor: "#e9ebee",
    }}
    src={source}
    fallbackSrc={util.avatarUrl()}
  />
);

export const NotificationAvatarImage = (
  notification: AppNotification,
  user: NotificationTrayUser,
) => {
  switch (notification.notificationType) {
    case "ideaCommented":
    case "ideaVoted":
    case "collaboratorAdded":
    case "mentionedDiscussionComment":
    case "mentionedIdeaComment":
    case "projectIdeaCommented":
    case "projectIdeaReplied":
    case "projectIdeaMoved":
    case "projectAssignedAssignee":
    case "projectAssignedManager":
    case "mentionedIdeaProjectComment":
    case "reactedIdeaComment":
    case "ideaBusinessProfileInvited":
    case "ideaCommentReply":
    case "groupUserJoined":
      return AvatarImage(util.avatarUrl(notification?.events?.[0]?.ownerUser));
    case "closingSoon":
    case "stageChanged":
    case "challengeClosed":
    case "challengeAddedToCalendarEvent":
    case "requestOpenChallenge":
    case "ideaSubmitted":
    case "challengeTranslate":
    case "aiChallengeFormat":
    case "aiFormatReview":
    case "challengeInnointelSearch":
    case "assessorAssigned":
      return AvatarImage(util.challengeImage(notification.ownerChallenge));
    case "aiIdeaFeedback":
    case "aiIdeaAnonymityCheck":
      return AvatarImage(
        util.ideaCoverImage(notification.ownerIdea, "200x200"),
      );
    case "ideaDeadlineApproaching":
    case "ideaDeadlineSet":
      return AvatarImage(
        util.ideaCoverImage(notification.events?.[0]?.ownerIdea, "200x200"),
      );
    case "reactedDiscussionComment":
    case "discussionCommentReply":
    case "discussionPostRequiresApproval":
    case "discussionPosted":
    case "discussionPollVoted":
    case "groupJoinRequested":
    case "groupInvited":
    case "groupAddedToCalendarEvent":
      return AvatarImage(
        util.groupCoverImage(
          notification.ownerGroup || notification.events?.[0]?.ownerGroup,
        ),
      );
    case "unlockedAchievement":
      return AvatarImage(util.avatarUrl(user ?? undefined));
    default:
      return null;
  }
};

export const NotificationContent = (notification: AppNotification) => {
  switch (notification.notificationType) {
    case "ideaCommented":
      return (
        <IdeaCommentedContent
          events={notification.events}
          ownerIdea={notification.ownerIdea}
        />
      );
    case "reactedIdeaComment":
      return (
        <CommentReacted
          events={notification.events}
          ownerIdea={notification.ownerIdea}
        />
      );
    case "ideaCommentReply":
      return (
        <IdeaCommentReply
          events={notification.events}
          ownerIdea={notification.ownerIdea}
        />
      );
    case "ideaDeadlineApproaching":
      return (
        <DeadlineApproaching
          events={notification.events}
          ownerIdea={notification.ownerIdea}
        />
      );
    case "ideaDeadlineSet":
      return (
        <DeadlineSet
          events={notification.events}
          ownerIdea={notification.ownerIdea}
        />
      );
    case "closingSoon":
      return (
        <ClosingSoonContent ownerChallenge={notification.ownerChallenge} />
      );
    case "stageChanged":
      return (
        <StageChangedContent ownerChallenge={notification.ownerChallenge} />
      );
    case "requestOpenChallenge":
      return (
        <RequestOpen
          events={notification.events}
          ownerChallenge={notification.ownerChallenge}
        />
      );
    case "challengeClosed":
      return <ChallengeClosed ownerChallenge={notification.ownerChallenge} />;
    case "discussionPosted":
      return (
        <DiscussionPostedContent
          ownerGroup={notification.ownerGroup}
          events={notification.events}
        />
      );
    case "ideaSubmitted":
      return (
        <IdeaSubmittedContent
          events={notification.events}
          ownerChallenge={notification.ownerChallenge}
        />
      );
    case "ideaVoted":
      return (
        <IdeaVotedContent
          events={notification.events}
          ownerIdea={notification.ownerIdea}
        />
      );
    case "collaboratorAdded":
      return (
        <AddedAsCollaboratorContent
          events={notification.events}
          ownerIdea={notification.ownerIdea}
        />
      );
    case "mentionedDiscussionComment":
    case "mentionedIdeaComment":
    case "mentionedIdeaProjectComment":
      return (
        <MentionedContent
          notification={notification}
          mentions={mentions}
          events={notification.events}
          notificationType={notification.notificationType}
        />
      );
    case "unlockedAchievement":
      return <UnlockedAchievementContent events={notification.events} />;
    case "projectIdeaCommented":
      return (
        <ProjectIdeaCommented
          events={notification.events}
          ownerIdea={notification.ownerIdea}
          ownerChallenge={notification.ownerChallenge}
        />
      );
    case "projectIdeaReplied":
      return (
        <ProjectIdeaReplied
          events={notification.events}
          ownerIdea={notification.ownerIdea}
          ownerChallenge={notification.ownerChallenge}
        />
      );
    case "projectIdeaMoved":
      return (
        <ProjectIdeaMoved
          events={notification.events}
          ownerIdea={notification.ownerIdea}
          ownerChallenge={notification.ownerChallenge}
        />
      );
    case "projectAssignedAssignee":
      return (
        <ProjectAssignedAssignee
          events={notification.events}
          ownerIdea={notification.ownerIdea}
          ownerChallenge={notification.ownerChallenge}
        />
      );
    case "projectAssignedManager":
      return (
        <ProjectAssignedManager
          events={notification.events}
          ownerIdea={notification.ownerIdea}
          ownerChallenge={notification.ownerChallenge}
        />
      );
    case "assessorAssigned":
      return (
        <AssessorAssignedContent
          events={notification.events}
          ownerChallenge={notification.ownerChallenge}
        />
      );
    case "challengeAddedToCalendarEvent":
      return (
        <ChallengeAddedToCalendar
          events={notification.events}
          ownerChallenge={notification.ownerChallenge}
        />
      );
    case "groupAddedToCalendarEvent":
      return (
        <GroupAddedToCalendar
          events={notification.events}
          ownerGroup={notification.ownerGroup}
        />
      );
    case "groupJoinRequested":
      return (
        <GroupJoinRequestedContent
          events={notification.events}
          ownerGroup={notification.ownerGroup}
        />
      );
    case "groupInvited":
      return (
        <GroupInvitedContent
          events={notification.events}
          ownerGroup={notification.ownerGroup}
        />
      );
    case "groupUserJoined":
      return (
        <Joined
          ownerGroup={notification.ownerGroup}
          events={notification.events}
        />
      );
    case "reactedDiscussionComment":
      return (
        <DiscussionReacted
          events={notification.events}
          ownerGroup={notification.ownerGroup}
        />
      );
    case "discussionCommentReply":
      return (
        <DiscussionReplied
          events={notification.events}
          ownerGroup={notification.ownerGroup}
        />
      );
    case "discussionPostRequiresApproval":
      return (
        <DiscussionApproval
          events={notification.events}
          ownerGroup={notification.ownerGroup}
        />
      );
    case "discussionPollVoted":
      return (
        <DiscussionPollVotedContent
          events={notification.events}
          ownerGroup={
            notification.ownerGroup || notification.events?.[0]?.ownerGroup
          }
        />
      );
    case "pollClosed":
      return <PollClosed context={notification.context} />;
    case "announcementReport":
      return (
        <AnnouncementReport
          context={notification.context}
          ownerChallenge={notification.ownerChallenge}
          ownerGroup={notification.ownerGroup}
        />
      );
    case "ideaBusinessProfileInvited":
      return (
        <IdeaBusinessProfileInvited
          events={notification.events}
          ownerIdeaBusinessProfile={notification.ownerIdeaBusinessProfile}
        />
      );
    case "challengeTranslate":
      return (
        <ChallengeTranslateContent
          events={notification.events}
          ownerChallenge={notification.ownerChallenge}
          status={notification.status}
        />
      );
    case "aiChallengeFormat":
      return (
        <AiChallengeFormatContent
          events={notification.events}
          ownerChallenge={notification.ownerChallenge}
          status={notification.status}
        />
      );
    case "aiFormatReview":
      return (
        <AiFormatReviewContent
          ownerChallenge={notification.ownerChallenge}
          status={notification.status}
        />
      );
    case "challengeInnointelSearch":
      return (
        <ChallengeInnointelSearchContent
          ownerChallenge={notification.ownerChallenge}
          status={notification.status}
        />
      );
    case "aiIdeaFeedback":
      return (
        <AiIdeaFeedbackContent
          ownerIdea={notification.ownerIdea}
          status={notification.status}
        />
      );
    case "aiIdeaAnonymityCheck":
      return (
        <AiIdeaAnonymityCheckContent
          ownerIdea={notification.ownerIdea}
          status={notification.status}
        />
      );

    default:
      return null;
  }
};

type NotificationActionsProps = NotificationContentProps & {
  notification: AppNotification;
  markNotificationAsInteracted?: (id: string) => void;
  setAnnouncementNotification?: React.Dispatch<
    React.SetStateAction<AnnouncementNotification | null>
  >;
  setAnnouncementModalOpen?: React.Dispatch<React.SetStateAction<boolean>>;
};

type NotificationItemProps = NotificationActionsProps & {
  user: NotificationTrayUser;
  onInvitationHandled?: (invitationId: string) => void;
};

const toAnnouncementNotification = (
  notification: AppNotification,
  events: NotificationEvent[],
): AnnouncementNotification => ({ ...notification, events });

export const NotificationActions = ({
  notification,
  invitations,
  onAccept,
  onReject,
}: NotificationActionsProps) => {
  const actionProps: NotificationContentProps = {
    events: notification.events,
    invitations,
    onAccept,
    onReject,
  };

  switch (notification.notificationType) {
    case "groupJoinRequested":
      return <JoinRequestedActions {...actionProps} />;
    case "groupInvited":
      return <InvitedActions {...actionProps} />;
    case "ideaBusinessProfileInvited":
      return <IdeaBusinessProfileInvitedActions {...actionProps} />;
    default:
      return null;
  }
};

function NotificationItem({
  notification,
  user,
  markNotificationAsInteracted,
  invitations,
  onInvitationHandled,
  setAnnouncementNotification,
  setAnnouncementModalOpen,
}: NotificationItemProps) {
  const { t } = useTranslation();
  const { notificationType, ownerGroup, interacted } = notification;

  if (!notificationType) return null;
  if (!(notificationAllowlist.indexOf(notificationType) > -1)) {
    return null;
  }
  if (notificationType === "discussedPosted" && !ownerGroup) {
    return null;
  }

  const menuItemStyle = {
    borderBottom: "1px solid",
    borderColor: "#E9EBEE",
    padding: ".833em 1em",
    borderRadius: 0,
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    textOverflow: "ellipsis",
    backgroundColor: interacted ? undefined : "#e9ebee",
    marginTop: 5,
    marginBottom: 5,
    width: "99%",
    color: "black",
  } satisfies React.CSSProperties;

  if (notification.notificationType === "ideaSendEmail") {
    return (
      <MenuItem
        onClick={() => {
          const subject = notification.context?.subject;
          const body = notification.context?.body;
          const announcementBody = typeof body === "string" ? body : "";
          setAnnouncementNotification?.(
            toAnnouncementNotification(notification, [
              {
                ownerAnnouncement: {
                  subject: typeof subject === "string" ? subject : "",
                  body: announcementBody,
                  createdAt: notification.createdAt,
                },
              },
            ]),
          );
          setAnnouncementModalOpen?.(true);
          markNotificationAsInteracted?.(notification._id);
        }}
        fitted
        link
        style={menuItemStyle}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minWidth: 50,
          }}
        >
          {AvatarImage(util.avatarUrl(notification?.events?.[0]?.ownerUser))}
        </div>
        <div style={{ marginLeft: 10, flex: 1 }}>
          <p
            style={{
              textAlign: "left",
              marginBottom: 0,
              lineHeight: "15px",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            <IdeaSendEmail
              events={notification.events}
              ownerIdea={notification.ownerIdea}
            />
          </p>
          <p style={{ color: "gray", marginTop: 3 }}>
            {formatDistanceToNow(new Date(notification.createdAt), {
              addSuffix: true,
            })}
          </p>
        </div>
      </MenuItem>
    );
  }

  if (notification.notificationType === "announcement")
    return (
      <MenuItem
        onClick={() => {
          setAnnouncementNotification?.(
            toAnnouncementNotification(notification, notification.events ?? []),
          );
          setAnnouncementModalOpen?.(true);
          markNotificationAsInteracted?.(notification._id);
        }}
        fitted
        link
        style={menuItemStyle}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minWidth: 50,
          }}
        >
          <Icon size="large" icon={MegaphoneIcon} />
        </div>
        <div style={{ marginLeft: 10, flex: 1 }}>
          <p
            style={{
              textAlign: "left",
              marginBottom: 0,
              lineHeight: "15px",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            <Trans
              i18nKey="notifications.newAnnouncement"
              values={{
                name: util.getEventNames(
                  notification.events ?? [],
                  "ownerAnnouncement",
                ),
                announcementText: util.pluralise(
                  notification.events?.length ?? 0,
                  t("generic.announcement").toLowerCase(),
                  t("generic.announcements").toLowerCase(),
                  false,
                ),
              }}
            />
          </p>
          <p style={{ color: "gray", marginTop: 3 }}>
            {formatDistanceToNow(new Date(notification.createdAt), {
              addSuffix: true,
            })}
          </p>
        </div>
      </MenuItem>
    );

  return (
    <MenuItem
      fitted
      link
      style={menuItemStyle}
      onClick={() => {
        markNotificationAsInteracted?.(notification._id);
        if (
          notification.notificationType === "aiChallengeFormat" &&
          notification.challenge
        ) {
          api.invalidateQueries(api.challenges.get, [notification.challenge]);
        }
        if (
          notification.notificationType === "aiFormatReview" &&
          notification.challenge
        ) {
          api.invalidateQueries(api.challenges.getChallengeFormatReview, [
            notification.challenge,
          ]);
        }
        if (
          (notification.notificationType === "aiIdeaFeedback" ||
            notification.notificationType === "aiIdeaAnonymityCheck") &&
          notification.idea
        ) {
          api.invalidateQueries(api.ideas.get, [notification.idea]);
        }
      }}
      asChild
    >
      <Link
        to={
          getNotificationLocation(notification, {
            pathname: window.location.pathname,
          })?.href ?? ""
        }
      >
        <div>
          {notification.status === "pending" ? (
            <Loader style={{ marginRight: 10, marginLeft: 8 }} />
          ) : (
            NotificationAvatarImage(notification, user)
          )}
        </div>
        <div style={{ marginLeft: 10, flex: 1 }}>
          <p
            style={{
              textAlign: "left",
              marginBottom: 0,
              lineHeight: "15px",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {NotificationContent(notification)}
            {notification.additionalEvents
              ? ` + ${notification.additionalEvents} more`
              : null}
          </p>
          <p style={{ color: "gray", marginTop: 3 }}>
            {formatDistanceToNow(new Date(notification.createdAt), {
              addSuffix: true,
            })}{" "}
            {notification.status === "failed" ? (
              <span style={{ color: "#e7381c", fontSize: 12, marginTop: -2 }}>
                {" "}
                - Task failed
              </span>
            ) : null}
          </p>
        </div>
        {actionableNotifications.indexOf(notificationType) > -1 ? (
          <div>
            <NotificationActions
              invitations={invitations}
              notification={notification}
              onAccept={onInvitationHandled}
              onReject={onInvitationHandled}
            />
          </div>
        ) : null}
      </Link>
    </MenuItem>
  );
}

export default withTranslation()(NotificationItem);
